/**
 * AppRoutes — Beta (pruned)
 * 
 * Core happy path only:
 * Auth → Marketplace → Checkout → Run → Dashboard
 */

import { lazy, Suspense } from "react";
import { Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { PortalGuard } from "@/components/portal/PortalGuard";

/** Redirects /app/signup → /auth while preserving query params */
function SignupRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/auth${search}`} replace />;
}

/** Wraps PortalShell with auth guard */
function PortalGuardedShell() {
  const PortalShellLazy = PortalShell as any;
  return (
    <PortalGuard>
      <PortalShellLazy />
    </PortalGuard>
  );
}

/** Redirect /portal/:slug → /portal/:slug/dashboard */
function PortalDashboardRedirect() {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/portal/${slug}/dashboard`} replace />;
}
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { RouteGuard } from "@/components/guards";

const DevHQWrapper = lazy(() => import("@/components/routing/DevHQWrapper").then(m => ({ default: m.DevHQWrapper })));

// Auth
const Auth = lazy(() => import("@/pages/exeibit/Auth"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));

// Profile & Upgrade
const Profile = lazy(() => import("@/pages/Profile"));
const Upgrade = lazy(() => import("@/pages/Upgrade"));

// Checkout & Delivery
const OfferCheckout = lazy(() => import("@/pages/checkout/OfferCheckout"));
const ThankYou = lazy(() => import("@/pages/checkout/ThankYou"));
const DeliveryPortal = lazy(() => import("@/pages/delivery/DeliveryPortal"));
const Success = lazy(() => import("@/pages/Success"));

// Landing
// LandingPage is eagerly loaded — it's the primary entry point, no code-split delay
import LandingPage from "@/pages/LandingPage";

// Legal
const Terms = lazy(() => import("@/pages/Terms"));
const RefundPolicy = lazy(() => import("@/pages/RefundPolicy"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Contact = lazy(() => import("@/pages/Contact"));
const Support = lazy(() => import("@/pages/Support"));
const FeaturesPage = lazy(() => import("@/pages/FeaturesPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const StatusPage = lazy(() => import("@/pages/Status"));
const PricingPage = lazy(() => import("@/pages/Pricing"));

// PWA Install
const Install = lazy(() => import("@/pages/Install"));

// Waitlist
const WaitlistPage = lazy(() => import("@/pages/WaitlistPage"));

// Design Partner Program
const DesignPartnerLanding = lazy(() => import("@/pages/DesignPartnerLanding"));

// White-label customer portal
const PortalShell = lazy(() => import("@/components/portal/PortalShell").then(m => ({ default: m.PortalShell })));
const PortalLogin = lazy(() => import("@/pages/portal/PortalLogin"));
const PortalDashboard = lazy(() => import("@/pages/portal/PortalDashboard"));
const PortalActivity = lazy(() => import("@/pages/portal/PortalActivity"));
const PortalSettings = lazy(() => import("@/pages/portal/PortalSettings"));

// 404
const NotFound = lazy(() => import("@/pages/NotFound"));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <LoadingSpinner />
  </div>
);

export function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* ROOT */}
        <Route path="/" element={<RouteErrorBoundary routeName="Landing"><LandingPage /></RouteErrorBoundary>} />
        <Route path="/install" element={<RouteErrorBoundary routeName="Install"><Install /></RouteErrorBoundary>} />

        {/* PUBLIC PAGES */}
        <Route path="/terms" element={<RouteErrorBoundary routeName="Terms"><Terms /></RouteErrorBoundary>} />
        <Route path="/refund-policy" element={<RouteErrorBoundary routeName="RefundPolicy"><RefundPolicy /></RouteErrorBoundary>} />
        <Route path="/contact" element={<RouteErrorBoundary routeName="Contact"><Contact /></RouteErrorBoundary>} />
        <Route path="/support" element={<RouteErrorBoundary routeName="Support"><Support /></RouteErrorBoundary>} />
        <Route path="/features" element={<RouteErrorBoundary routeName="Features"><FeaturesPage /></RouteErrorBoundary>} />
        <Route path="/about" element={<RouteErrorBoundary routeName="About"><AboutPage /></RouteErrorBoundary>} />
        <Route path="/status" element={<RouteErrorBoundary routeName="Status"><StatusPage /></RouteErrorBoundary>} />
        <Route path="/privacy" element={<RouteErrorBoundary routeName="Privacy"><Privacy /></RouteErrorBoundary>} />

        {/* WAITLIST */}
        <Route path="/waitlist" element={<RouteErrorBoundary routeName="Waitlist"><WaitlistPage /></RouteErrorBoundary>} />

        {/* DESIGN PARTNER PROGRAM */}
        <Route path="/design-partners" element={<RouteErrorBoundary routeName="DesignPartners"><DesignPartnerLanding /></RouteErrorBoundary>} />

        {/* AUTH */}
        <Route path="/auth" element={<RouteErrorBoundary routeName="Auth"><Auth /></RouteErrorBoundary>} />
        <Route path="/forgot-password" element={<RouteErrorBoundary routeName="ForgotPassword"><ForgotPassword /></RouteErrorBoundary>} />
        <Route path="/reset-password" element={<RouteErrorBoundary routeName="ResetPassword"><ResetPassword /></RouteErrorBoundary>} />
        <Route path="/verify-email" element={<RouteErrorBoundary routeName="VerifyEmail"><VerifyEmail /></RouteErrorBoundary>} />
        <Route path="/login" element={<Navigate to="/auth" replace />} />
        <Route path="/register" element={<Navigate to="/auth" replace />} />

        {/* PROFILE & UPGRADE */}
        <Route path="/settings" element={<RouteGuard><RouteErrorBoundary routeName="Profile"><Profile /></RouteErrorBoundary></RouteGuard>} />
        <Route path="/profile" element={<RouteGuard><RouteErrorBoundary routeName="Profile"><Profile /></RouteErrorBoundary></RouteGuard>} />
        <Route path="/upgrade" element={<RouteGuard><RouteErrorBoundary routeName="Upgrade"><Upgrade /></RouteErrorBoundary></RouteGuard>} />

        {/* CHECKOUT & DELIVERY */}
        <Route path="/checkout/:offerSlug" element={<RouteErrorBoundary routeName="OfferCheckout"><OfferCheckout /></RouteErrorBoundary>} />
        <Route path="/thank-you" element={<RouteErrorBoundary routeName="ThankYou"><ThankYou /></RouteErrorBoundary>} />
        <Route path="/delivery/:deliveryId" element={<RouteErrorBoundary routeName="DeliveryPortal"><DeliveryPortal /></RouteErrorBoundary>} />
        <Route path="/success" element={<RouteErrorBoundary routeName="Success"><Success /></RouteErrorBoundary>} />
        <Route path="/subscription-success" element={<Navigate to="/success" replace />} />
        <Route path="/payment-success" element={<Navigate to="/success" replace />} />
        <Route path="/payment-cancel" element={<Navigate to="/pricing?canceled=true" replace />} />

        {/* SIGNUP REDIRECT — preserve query params for intent-based routing */}
        <Route path="/app/signup" element={<SignupRedirect />} />

        {/* UNIFIED PLATFORM */}
        <Route path="/app/*" element={<DevHQWrapper />} />

        {/* WHITE-LABEL CUSTOMER PORTAL — branded per reseller, zero Nodistar references */}
        <Route path="/portal/login" element={<RouteErrorBoundary routeName="PortalLogin"><PortalLogin /></RouteErrorBoundary>} />
        <Route path="/portal/:slug/login" element={<RouteErrorBoundary routeName="PortalLogin"><PortalLogin /></RouteErrorBoundary>} />
        <Route path="/portal" element={<PortalGuardedShell />}>
          <Route index element={<Navigate to="/portal/dashboard" replace />} />
          <Route path="dashboard" element={<PortalDashboard />} />
          <Route path="activity" element={<PortalActivity />} />
          <Route path="settings" element={<PortalSettings />} />
        </Route>
        <Route path="/portal/:slug" element={<PortalGuardedShell />}>
          <Route index element={<PortalDashboardRedirect />} />
          <Route path="dashboard" element={<PortalDashboard />} />
          <Route path="activity" element={<PortalActivity />} />
          <Route path="settings" element={<PortalSettings />} />
        </Route>

        {/* LEGACY REDIRECTS → /app */}
        <Route path="/dashboard" element={<Navigate to="/app" replace />} />
        <Route path="/my-exeibits" element={<Navigate to="/app" replace />} />
        <Route path="/my-exeibits/*" element={<Navigate to="/app" replace />} />
        <Route path="/my-sales" element={<Navigate to="/app" replace />} />
        <Route path="/my/*" element={<Navigate to="/app" replace />} />
        <Route path="/sub-account" element={<Navigate to="/app" replace />} />
        <Route path="/workspace" element={<Navigate to="/app" replace />} />
        <Route path="/workspace/*" element={<Navigate to="/app" replace />} />
        <Route path="/hq" element={<Navigate to="/app" replace />} />
        <Route path="/hq/*" element={<Navigate to="/app" replace />} />
        <Route path="/admin" element={<Navigate to="/app/admin" replace />} />
        <Route path="/admin/*" element={<Navigate to="/app/admin" replace />} />
        <Route path="/learn/*" element={<Navigate to="/app" replace />} />
        <Route path="/e-bits" element={<Navigate to="/app" replace />} />
        <Route path="/e-bits/*" element={<Navigate to="/app" replace />} />
        <Route path="/stacks" element={<Navigate to="/app" replace />} />
        <Route path="/stacks/*" element={<Navigate to="/app" replace />} />
        <Route path="/sandbox" element={<Navigate to="/app" replace />} />
        <Route path="/sandbox/*" element={<Navigate to="/app" replace />} />
        <Route path="/services" element={<Navigate to="/app" replace />} />
        <Route path="/services/*" element={<Navigate to="/app" replace />} />
        <Route path="/managed" element={<Navigate to="/app" replace />} />
        <Route path="/leadgen" element={<Navigate to="/app" replace />} />
        <Route path="/pricing" element={<RouteErrorBoundary routeName="Pricing"><PricingPage /></RouteErrorBoundary>} />
        <Route path="/leads" element={<Navigate to="/app" replace />} />
        <Route path="/video-queue" element={<Navigate to="/app" replace />} />
        <Route path="/video-queue/*" element={<Navigate to="/app" replace />} />
        <Route path="/intake" element={<Navigate to="/app" replace />} />
        <Route path="/tools/*" element={<Navigate to="/app" replace />} />
        <Route path="/q/*" element={<Navigate to="/app" replace />} />
        <Route path="/b/*" element={<Navigate to="/app" replace />} />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
